import { useMemo } from "react";
import type { Auction, AuctionBid } from "@/services/types";
import { Modal } from "@/components/base/Modal";
import { Button } from "@/components/base/Button";
import { Alert } from "@/components/base/Alert";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { computeAuctionResult } from "@/utils/auction";

interface CloseAuctionModalProps {
  open: boolean;
  onClose: () => void;
  auction: Auction;
  winnerBid: AuctionBid | null;
  onConfirm: () => void;
  closing: boolean;
  actor: string;
}

/**
 * The close-auction confirmation. Shows the complete outcome before anything is
 * committed and states plainly that closing is permanent. The authoritative
 * result is computed on the backend — this is a faithful preview of what it will
 * return, using the current lowest standing bid.
 */
export function CloseAuctionModal({ open, onClose, auction, winnerBid, onConfirm, closing, actor }: CloseAuctionModalProps) {
  const preview = useMemo(() => {
    if (!winnerBid) return null;
    const winner = auction.eligible.find((e) => e.memberId === winnerBid.memberId);
    return computeAuctionResult({
      pool: auction.pool,
      monthlyInstallment: auction.monthlyInstallment,
      memberCount: auction.memberCount,
      dividendTiming: auction.dividendTiming,
      winnerMemberId: winnerBid.memberId,
      winningBid: winnerBid.amount,
      winnerPendingDues: winner?.outstanding ?? 0,
      closedBy: actor,
      closedAt: new Date().toISOString(),
    });
  }, [winnerBid, auction, actor]);

  if (!winnerBid || !preview) return null;
  const winner = auction.eligible.find((e) => e.memberId === winnerBid.memberId);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Close this auction?"
      description="This decision is permanent and cannot be reversed."
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={closing}>
            Keep bidding
          </Button>
          <Button variant="danger" icon={<i className="ri-lock-line" />} loading={closing} onClick={onConfirm}>
            Close auction
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Alert variant="warning" title="What closing does">
          The winner is locked in permanently and can never win again in this group. Installment demands are generated
          for every member at the reduced amount, and a payout record is created for the winner. This cannot be undone.
        </Alert>

        <div className="rounded-md bg-accent-100 px-4 py-3">
          <p className="text-xs font-medium uppercase tracking-wide text-accent-800">Winning member</p>
          <p className="text-lg font-semibold text-accent-900">{winner?.fullName ?? "—"}</p>
          <p className="tnum text-sm text-accent-800">
            {winner?.ticketNumber ?? ""} · {auction.periodLabel} · cycle {auction.cycle}
          </p>
        </div>

        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
          <div className="sm:col-span-3 border-t border-background-100 pt-2 flex items-center justify-between">
            <dt className="text-foreground-600">Amount {winner?.fullName?.split(" ")[0] ?? "winner"} will receive</dt>
            <dd className="tnum text-lg font-bold text-accent-900"><CurrencyDisplay amount={preview.winningBid} hideSymbol /></dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-foreground-600">Discount generated</dt>
            <dd className="tnum font-medium text-danger-600"><CurrencyDisplay amount={preview.discount} hideSymbol /></dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-foreground-600">Dividend / member</dt>
            <dd className="tnum font-medium text-foreground-900"><CurrencyDisplay amount={preview.perMemberDividend} hideSymbol /></dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-foreground-600">Net installment</dt>
            <dd className="tnum font-medium text-foreground-900"><CurrencyDisplay amount={preview.netInstallment} hideSymbol /></dd>
          </div>
          {preview.dividendRemainder > 0 ? (
            <div className="flex items-center justify-between">
              <dt className="text-warning-700">Rounding remainder</dt>
              <dd className="tnum font-medium text-warning-700"><CurrencyDisplay amount={preview.dividendRemainder} hideSymbol /></dd>
            </div>
          ) : null}
          <div className="flex items-center justify-between">
            <dt className="text-foreground-600">Total expected collection</dt>
            <dd className="tnum font-bold text-primary-700"><CurrencyDisplay amount={preview.totalCollected} hideSymbol /></dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-foreground-600">Winner's pending dues</dt>
            <dd className="tnum font-medium text-danger-600"><CurrencyDisplay amount={preview.winnerPendingDues} hideSymbol /></dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-foreground-600">Net payout to winner</dt>
            <dd className="tnum font-bold text-foreground-950"><CurrencyDisplay amount={preview.winnerPayout} hideSymbol /></dd>
          </div>
        </dl>

        <p className="text-xs text-foreground-500">
          {preview.dividendRemainder === 0
            ? "Collection exactly equals the payout — nothing is left over."
            : `Collection is the payout plus a rounding remainder of ${preview.dividendRemainder.toLocaleString("en-IN")}, reconciled separately.`}
        </p>
      </div>
    </Modal>
  );
}